import { useState, useRef, useEffect } from "react";
import { db, storage } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import AddressAutocomplete from "./AddressAutocomplete";

function compressImage(file, maxWidth = 800, quality = 0.7) {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PostFood({ user, location, onRequestMapPick, mapPickResult, onConsumedMapPickResult }) {
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [hoursLeft, setHoursLeft] = useState("");
  const [perishable, setPerishable] = useState(1);
  const [address, setAddress] = useState("");
  const [postLat, setPostLat] = useState(null);
  const [postLng, setPostLng] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleAddressSelect = ({ address: addr, lat, lng }) => {
    setAddress(addr);
    setPostLat(lat);
    setPostLng(lng);
  };

  useEffect(() => {
    if (mapPickResult && onConsumedMapPickResult) {
      setAddress(mapPickResult.address);
      setPostLat(mapPickResult.lat);
      setPostLng(mapPickResult.lng);
      onConsumedMapPickResult();
    }
  }, [mapPickResult, onConsumedMapPickResult]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setUploadError(null);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lat = postLat ?? location?.lat;
    const lng = postLng ?? location?.lng;
    if (!lat || !lng) {
      alert("Please select a pickup address from the suggestions so we can map your location.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      let imageUrl = null;
      if (imageFile) {
        const compressed = await compressImage(imageFile);
        const fileName = `food_images/${Date.now()}_${imageFile.name.replace(/\.[^.]+$/, ".jpg")}`;
        const imageRef = ref(storage, fileName);
        await uploadBytes(imageRef, compressed, { contentType: "image/jpeg" });
        imageUrl = await getDownloadURL(imageRef);
      }
      await addDoc(collection(db, "food_posts"), {
        name,
        quantity: Number(quantity),
        hoursLeft: Number(hoursLeft),
        perishable,
        address,
        lat,
        lng,
        imageUrl,
        createdAt: Date.now(),
        userId: user.uid
      });
      setName("");
      setQuantity("");
      setHoursLeft("");
      setAddress("");
      setPostLat(null);
      setPostLng(null);
      removeImage();
    } catch (err) {
      console.error("Post failed:", err);
      setUploadError(err.message || "Something went wrong. Please try again.");
    }
    setUploading(false);
  };

  return (
    <div>
      {postLat && <p className="text-xs text-green-600 mb-3">Address location set</p>}
      {!postLat && location && <p className="text-xs text-green-600 mb-3">Using your current location</p>}
      <form onSubmit={handleSubmit} className="space-y-2.5">
        <input className="w-full p-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400" placeholder="Food name" value={name} onChange={e => setName(e.target.value)} />
        <AddressAutocomplete value={address} onChange={setAddress} onSelect={handleAddressSelect} />
        {onRequestMapPick && (
          <button type="button" onClick={onRequestMapPick} className="btn-pop w-full p-2.5 border-2 border-dashed border-green-300 rounded-xl text-xs text-green-600 hover:bg-green-50 font-medium">
            📍 Or pick pickup location on map
          </button>
        )}
        <div>
          {imagePreview ? (
            <div className="relative">
              <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-xl border border-gray-200" />
              <button type="button" onClick={removeImage} className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center text-xs">✕</button>
            </div>
          ) : (
            <button type="button" onClick={() => fileInputRef.current?.click()} className="btn-pop w-full p-3 border-2 border-dashed border-gray-300 rounded-xl text-xs text-gray-400 hover:border-green-400">
              📷 Add a photo (optional)
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
        </div>
        {uploadError && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{uploadError}</p>}
        <div className="grid grid-cols-2 gap-2">
          <input className="w-full p-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400" type="number" placeholder="Quantity" value={quantity} onChange={e => setQuantity(e.target.value)} />
          <input className="w-full p-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400" type="number" placeholder="Hours left" value={hoursLeft} onChange={e => setHoursLeft(e.target.value)} />
        </div>
        <select className="w-full p-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400" onChange={e => setPerishable(Number(e.target.value))}>
          <option value={1}>Perishable</option>
          <option value={0}>Non-perishable</option>
        </select>
        <button disabled={uploading} className="btn-pop w-full bg-green-500 text-white p-2.5 rounded-xl text-sm font-semibold hover:bg-green-600 disabled:opacity-50">
          {uploading ? "Posting..." : "Post Food"}
        </button>
      </form>
    </div>
  );
}
